import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import ProductCard from '@/components/ProductCard';
import SEO from '@/components/SEO';
import axios from 'axios';
import { ChevronRight, Filter, X, Ruler } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Common tile dimensions for quick filters
const DIMENSION_FILTERS = [
  { label: 'Toate', value: 'all' },
  { label: '60x60', value: '60x60' },
  { label: '60x120', value: '60x120' },
  { label: '20x120', value: '20x120' },
  { label: '15x60', value: '15x60' },
  { label: '30x60', value: '30x60' },
  { label: '80x80', value: '80x80' },
  { label: '20x60', value: '20x60' },
];

// Extract dimension from product data
function getProductDim(product) {
  const text = [
    product.name || '',
    product.code || '',
    ...(product.technical_specs || []).map(s => `${s.name || ''} ${s.value || ''}`)
  ].join(' ');
  const m = text.match(/(\d{2,3})\s*[x×X]\s*(\d{2,3})/);
  if (m) {
    const w = parseInt(m[1]), h = parseInt(m[2]);
    return `${Math.min(w,h)}x${Math.max(w,h)}`;
  }
  return null;
}

const PlaciCeramicePage = () => {
  const { subcategory } = useParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(subcategory || 'all');
  const [activeDim, setActiveDim] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  const [cms, setCms] = useState({});

  const subcategories = [
    { slug: 'all', name: 'Toate' },
    { slug: 'gresie', name: 'Gresie' },
    { slug: 'faianta', name: 'Faianță' },
  ];

  useEffect(() => {
    fetchProducts();
  }, [subcategory]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const [prodRes, cmsRes] = await Promise.all([
        axios.get(subcategory ? `${API_URL}/api/products?subcategory=${subcategory}` : `${API_URL}/api/products?category=placi-ceramice`),
        axios.get(`${API_URL}/api/pages/ceramice`)
      ]);
      setProducts(prodRes.data);
      if (cmsRes.data.content) setCms(cmsRes.data.content);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = (slug) => {
    setActiveFilter(slug);
    setActiveDim('all');
    setShowFilters(false);
  };

  // Available dimensions in current products
  const availableDims = useMemo(() => {
    const dims = new Set();
    products.forEach(p => {
      const dim = getProductDim(p);
      if (dim) dims.add(dim);
    });
    return dims;
  }, [products]);

  // Filter products by subcategory and dimension
  const filteredProducts = useMemo(() => {
    let result = activeFilter === 'all' ? products : products.filter(p => p.subcategory_slug === activeFilter);
    if (activeDim !== 'all') {
      result = result.filter(p => getProductDim(p) === activeDim);
    }
    return result;
  }, [products, activeFilter, activeDim]);

  // Only show dimension filters that have products
  const visibleDimFilters = DIMENSION_FILTERS.filter(
    d => d.value === 'all' || availableDims.has(d.value)
  );

  return (
    <Layout>
      <SEO
        title={cms.hero_title || 'Plăci Ceramice - Gresie & Faianță Brașov'}
        description={cms.hero_description || 'Gresie și faianță premium la cele mai bune prețuri. Plăci ceramice 60x120, 60x60, 20x120 pentru living, baie și bucătărie. Shagun Surfaces Brașov.'}
        canonicalUrl={`https://shagunsurfaces.ro/placi-ceramice${subcategory ? '/' + subcategory : ''}`}
      />
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-[#050505]" data-testid="placi-ceramice-hero">
        {cms.hero_image && (
          <div className="absolute inset-0">
            <img src={cms.hero_image.startsWith('/api') ? `${API_URL}${cms.hero_image}` : cms.hero_image} alt="" className="w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-b from-[#050505]/60 to-[#050505]" />
          </div>
        )}
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <nav className="flex items-center gap-2 text-sm mb-8">
            <Link to="/" className="text-[#A3A3A3] hover:text-[#D4AF37]">Acasă</Link>
            <ChevronRight size={14} className="text-[#A3A3A3]" />
            <span className="text-[#D4AF37]">Plăci Ceramice</span>
            {subcategory && (
              <>
                <ChevronRight size={14} className="text-[#A3A3A3]" />
                <span className="text-white capitalize">{subcategory}</span>
              </>
            )}
          </nav>

          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6">
            {cms.hero_title || 'Plăci Ceramice'}
          </h1>
          <p className="text-[#A3A3A3] text-lg max-w-2xl">
            {cms.hero_description || 'Gresie și faianță de înaltă calitate. Aducem orice model, la cel mai bun preț.'}
          </p>
        </div>
      </section>

      {/* Filters & Products */}
      <section className="py-12 bg-[#0a0a0a]" data-testid="products-section">
        <div className="max-w-7xl mx-auto px-6">
          {/* Category Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div className="flex items-center gap-4">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="lg:hidden flex items-center gap-2 text-[#A3A3A3] border border-white/10 px-4 py-2"
              >
                <Filter size={18} />
                Filtrează
              </button>
              
              <div className="hidden lg:flex items-center gap-2">
                {subcategories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={cat.slug === 'all' ? '/placi-ceramice' : `/placi-ceramice/${cat.slug}`}
                    onClick={() => handleFilter(cat.slug)}
                    className={`px-6 py-2 text-sm font-medium transition-all ${
                      activeFilter === cat.slug
                        ? 'bg-[#D4AF37] text-black'
                        : 'bg-[#121212] text-[#A3A3A3] hover:text-white border border-white/10'
                    }`}
                    data-testid={`filter-${cat.slug}`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>

            <p className="text-[#A3A3A3] text-sm">
              {filteredProducts.length} produse
            </p>
          </div>

          {/* Dimension Filters */}
          {visibleDimFilters.length > 1 && (
            <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="dimension-filters">
              <Ruler size={14} className="text-[#D4AF37] mr-1" />
              <span className="text-[#666] text-xs mr-1">Dimensiune:</span>
              {visibleDimFilters.map((dim) => (
                <button
                  key={dim.value}
                  onClick={() => setActiveDim(dim.value)}
                  className={`px-3 py-1.5 text-xs font-medium transition-all rounded ${
                    activeDim === dim.value
                      ? 'bg-[#D4AF37] text-black'
                      : 'bg-[#1a1a1a] text-[#A3A3A3] hover:text-white border border-white/10 hover:border-white/20'
                  }`}
                  data-testid={`dim-filter-${dim.value}`}
                >
                  {dim.label}{dim.value !== 'all' ? ' cm' : ''}
                </button>
              ))}
            </div>
          )}

          {/* Mobile Filters Modal */}
          {showFilters && (
            <div className="fixed inset-0 z-50 lg:hidden">
              <div className="absolute inset-0 bg-black/80" onClick={() => setShowFilters(false)} />
              <div className="absolute bottom-0 left-0 right-0 bg-[#121212] p-6 rounded-t-2xl">
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-white font-serif text-xl">Filtrează</h3>
                  <button onClick={() => setShowFilters(false)} className="text-[#A3A3A3]">
                    <X size={24} />
                  </button>
                </div>
                <p className="text-[#666] text-xs mb-2">Categorie:</p>
                <div className="flex flex-col gap-2 mb-4">
                  {subcategories.map((cat) => (
                    <Link
                      key={cat.slug}
                      to={cat.slug === 'all' ? '/placi-ceramice' : `/placi-ceramice/${cat.slug}`}
                      onClick={() => handleFilter(cat.slug)}
                      className={`px-6 py-4 text-center font-medium transition-all ${
                        activeFilter === cat.slug
                          ? 'bg-[#D4AF37] text-black'
                          : 'bg-[#050505] text-[#A3A3A3] border border-white/10'
                      }`}
                    >
                      {cat.name}
                    </Link>
                  ))}
                </div>
                {visibleDimFilters.length > 1 && (
                  <>
                    <p className="text-[#666] text-xs mb-2">Dimensiune:</p>
                    <div className="flex flex-wrap gap-2">
                      {visibleDimFilters.map((dim) => (
                        <button
                          key={dim.value}
                          onClick={() => { setActiveDim(dim.value); setShowFilters(false); }}
                          className={`px-4 py-2 text-sm font-medium transition-all ${
                            activeDim === dim.value
                              ? 'bg-[#D4AF37] text-black'
                              : 'bg-[#050505] text-[#A3A3A3] border border-white/10'
                          }`}
                        >
                          {dim.label}{dim.value !== 'all' ? ' cm' : ''}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Products Grid */}
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="bg-[#121212] aspect-square animate-pulse" />
              ))}
            </div>
          ) : filteredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProducts.map((product, index) => (
                <ProductCard key={product.id} product={product} index={index} />
              ))}
            </div>
          ) : (
            <div className="text-center py-20">
              <p className="text-[#A3A3A3] text-lg mb-4">
                {activeDim !== 'all' 
                  ? `Nu avem produse cu dimensiunea ${activeDim} cm în această categorie.`
                  : 'Momentan nu avem produse în această categorie.'}
              </p>
              {activeDim !== 'all' && (
                <button onClick={() => setActiveDim('all')} className="text-[#D4AF37] hover:underline text-sm">
                  Arată toate dimensiunile
                </button>
              )}
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
};

export default PlaciCeramicePage;
